import { AfterViewInit, Component, computed, inject, Input, linkedSignal, OnInit, Signal, signal, ViewChild } from '@angular/core';
import {
  AddressDTO,
  API,
  AttributeMapDTO,
  AttributeType,
  CompanyDTO,
  Order,
  pm_Product_retrieveAll_order,
  ProductCategoryFullDTO,
  ProductFullDTO,
} from '../../../services/api/api';
import { ModalNavigationService } from '../../../services/gui/modal/modal-navigation.service';
import { ErrorService } from '../../../services/error.service';
import { UtilService } from '../../../services/util.service';
import { TagService } from '../../../services/tag.service';
import { ProductFullDTOAndTags } from '../../../models/taggable.model';
import { forkJoin, from, of, switchMap } from 'rxjs';
import {
  IDAndTypeInterface,
  OrderOption,
  SbProductListColumn,
  SbSplitButton,
  SelectionState,
} from '../../../services/global/global-interfaces';
import { PermissionService } from '../../../services/permission.service';
import { finalize, take } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { createAttributeColumns, getPropsFromDTO, isColumnMatch } from '../../../services/global/global-methods';
import { clientColumnDefaultsProducts } from './clientColumnDefaultsProducts';
import { DynamicTableBase } from '../../../components/dynamic-table-elements/DynamicTableBase';
import { FilterPanelComponent } from '@ngx-seatback/layout';
import { SbSplitButtonItem, SearchOptions } from '@ngx-seatback/controls';
import { ModalNavigationComponent } from '../../../services/gui/modal/modal-navigation-component';
import { MultiSelectButtonAction } from '../../../components/table-multi-select-button/table-multi-select-button.component';
import { RoutingID } from '../../../routes/sb-routing-enum';

type SplitButtonAction = 'delete';
type ProductFullDTOAndTagsAndButtons = ProductFullDTOAndTags & SbSplitButton<SplitButtonAction>;

@Component({
  selector: 'sb-page-products',
  templateUrl: './products.component.html',
  styleUrls: ['./products.component.scss'],
  standalone: false,
})
export class ProductsComponent extends DynamicTableBase<SbProductListColumn> implements OnInit, AfterViewInit, ModalNavigationComponent {
  // injects
  protected readonly api = inject(API);
  private readonly modalNavigation = inject(ModalNavigationService);
  public readonly errorHandler = inject(ErrorService);
  public readonly util = inject(UtilService);
  public readonly tagService = inject(TagService);
  public readonly permissionService = inject(PermissionService);
  // global reference
  protected window = window;
  @ViewChild('FilterPanelRef') filterPanelRef: FilterPanelComponent;

  @Input() isInModalContext: boolean = false;
  @Input() isInIntegratedContext: boolean = false;
  modalType: string = '';

  /* filter Variables */
  // search
  search: string | null = null;
  exactMatch: SearchOptions = 'contains';
  // ID filtering
  selectedCompany: CompanyDTO | null = null;
  filteredProductCategories: ProductCategoryFullDTO[] = [];

  orderOption = signal<OrderOption<SbProductListColumn>>({
    direction: Order.asc,
    value: {
      id: 'id',
      title: 'ID',
      selected: false,

      type: 'PROPERTY',
      orderId: pm_Product_retrieveAll_order.ID,
      canOrder: true,
    },
  });
  filterPanelOrderOption = linkedSignal<OrderOption<SbProductListColumn>, OrderOption<SbProductListColumn>>({
    source: this.orderOption,
    computation: orderOption => {
      return { ...orderOption };
    },
  });
  allOrderOptions: OrderOption<SbProductListColumn>[] = [];

  /* global */
  productsList = signal<ProductFullDTOAndTagsAndButtons[]>([]);
  selectedProducts = signal<Map<number, ProductFullDTOAndTagsAndButtons>>(new Map());
  selectionState: Signal<SelectionState> = computed(() => {
    const currentProducts = this.productsList();
    const currentlySelectedProducts = this.selectedProducts();
    if (!currentProducts || currentProducts.length === 0) {
      return 'none';
    }
    const allSelectedOnCurrentPage = currentProducts.every(item => currentlySelectedProducts.has(item.id));
    if (allSelectedOnCurrentPage) {
      return 'all';
    }
    const someSelectedOnCurrentPage = currentProducts.some(item => currentlySelectedProducts.has(item.id));
    if (someSelectedOnCurrentPage) {
      return 'partly';
    }
    return 'none';
  });

  constructor() {
    super();
    // has to be called before resetOrderOption to initialize columns
    this.columnDefaults = clientColumnDefaultsProducts[this.util.getSeatbackCMSClient()] ?? clientColumnDefaultsProducts['generic'] ?? [];
    this.resetOrderOption();
  }

  ngOnInit() {
    if (this.isInModalContext) {
      this.modalType = this.modalNavigation.getCurrentNavigation()?.data?.modalType;
      const map = new Map<number, ProductFullDTOAndTagsAndButtons>(
        (this.modalNavigation.getCurrentNavigation()?.data?.selectedProducts ?? []).map((product: ProductFullDTO) => [product.id, product])
      );
      this.selectedProducts.set(map);
    }

    const attributeTypes: AttributeType[] = [];
    if (this.permissionService.hasPermission(this.permissionService.Permission.product_attribute_datatype_list)) {
      attributeTypes.push(AttributeType.Product);
    }
    of(attributeTypes)
      .pipe(
        switchMap((types: AttributeType[]) =>
          forkJoin({
            attributeMap: this.api.shared_Attribute_retrieve({ type: types }),
            preselectedColumns: from(this.loadSelectedColumns('productListSelectedColumnsV1')),
          })
        ),
        take(1),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (forkJoinResult: { attributeMap: AttributeMapDTO; preselectedColumns: IDAndTypeInterface[] | null }) => {
          this.initColumns(forkJoinResult.attributeMap, forkJoinResult.preselectedColumns);
          this.requestDebounced();
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
  }

  ngAfterViewInit() {
    // subscription for filterValue deselect
    this.initTagSubscription();
  }

  // order methods

  resetOrderOption() {
    // reset to default ordering, copy object properties to avoid changing original default
    this.orderOption.set({
      value: this.columns.find(col => col.id === 'id'),
      direction: Order.asc,
    });
  }

  setToOrderAndRefresh(pValue: SbProductListColumn) {
    this.setToOrderOption(pValue);
    this.requestDebounced();
  }

  private setToOrderOption(pValue: SbProductListColumn) {
    if (!pValue) {
      // nothing is set fallback to default
      this.resetOrderOption();
      return;
    }
    const currentOrderOption = this.orderOption();
    if (currentOrderOption.value === pValue) {
      if (currentOrderOption.direction === Order.asc) {
        this.orderOption.set({ ...currentOrderOption, direction: Order.desc });
      } else {
        this.orderOption.set({ ...currentOrderOption, direction: Order.asc });
      }
    } else {
      this.orderOption.set({ value: pValue, direction: Order.asc });
    }
  }

  getOrderOptions(): { by: pm_Product_retrieveAll_order; param?: string; direction: Order } {
    // if no orderOption -> return default order
    const currentOrderOption = this.orderOption();
    if (!currentOrderOption.value?.type) {
      this.resetOrderOption();
      return {
        by: pm_Product_retrieveAll_order.ID,
        direction: Order.asc,
      };
    }

    // if value equals column title
    switch (currentOrderOption.value.type) {
      case 'PRODUCT_ATTRIBUTE':
        return {
          by: pm_Product_retrieveAll_order.ATTRIBUTES,
          param: currentOrderOption.value.id,
          direction: currentOrderOption.direction,
        };
      case 'PROPERTY':
        return {
          by: currentOrderOption.value.orderId as pm_Product_retrieveAll_order,
          direction: currentOrderOption.direction,
        };
      default:
        this.resetOrderOption();
        return {
          by: pm_Product_retrieveAll_order.ID,
          direction: Order.asc,
        };
    }
  }

  isFilterClassActive(column: SbProductListColumn): boolean {
    if (column.type === 'PROPERTY') {
      const id = column.id;
      const canFilter = column?.canFilter;

      const propertyFilters = {
        // pool: this.filteredPools?.length > 0,
        // location: this.filteredLocations?.length > 0,
        // channel: this.filteredChannels?.length > 0,
        default: canFilter && this.columnFiltersMap?.has(id),
      };

      return propertyFilters[id] ?? propertyFilters.default;
    }

    const filterValue = this.columnFiltersMap?.get(column.id);

    return this.stringTransformation.stringNotNull(filterValue?.value);
  }

  // request
  refreshFromServer() {
    if (!this.pagination) {
      this.loading.set(false);
      return;
    }
    const categoryIds = this.filteredProductCategories?.length > 0 ? this.filteredProductCategories.map(cat => cat.id) : null;
    const sub = this.api
      .pm_Product_retrieveAll(
        {
          search: this.stringTransformation.encodeSearchQueryParamWithOptions(this.exactMatch, this.search),
          manufacturer_id: this.selectedCompany ? [this.selectedCompany.id] : null,
          productCategory_id: categoryIds,
          order: this.getOrderOptions(),
        },
        this.pagination
      )
      .pipe(
        switchMap(prods => this.tagService.getAndAddTagEntitiesObs(this.tagService.TaggableDTO.Product, prods)),
        take(1),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loading.set(false);
        })
      )
      .subscribe({
        next: (products: ProductFullDTOAndTags[]) => {
          this.productsList.set(this.buildSplitButtons(products));
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
    this.subs.push(sub);
  }

  // Button methods
  buildSplitButtons<T extends { id: any }>(itemsToAdd: T[]): (T & SbSplitButton<SplitButtonAction>)[] {
    const result = itemsToAdd.map(item => {
      return { ...item, sbButtons: [] as SbSplitButtonItem<SplitButtonAction>[] };
    });
    result.forEach(item => {
      if (this.permissionService.hasPermissions([this.permissionService.Permission.product_show])) {
        item.sbButtons.push({
          icon: 'details',
          tooltip: 'lang.globals.details',
          link: RoutingID.productDetails,
          pathParams: [{ id: 'productID', value: item?.id }],
          queryParams: { returnTo: this.util.getPageLink() },
        });
      }
      if (this.permissionService.hasPermissions([this.permissionService.Permission.product_edit])) {
        item.sbButtons.push({
          icon: 'edit',
          tooltip: 'lang.globals.edit',
          link: RoutingID.productEdit,
          pathParams: [{ id: 'productID', value: item?.id }],
          queryParams: { returnTo: this.util.getPageLink() },
        });
      }
    });
    return result;
  }

  // selection methods

  returnSelectedProducts() {
    this.modalNavigation.result(Array.from(this.selectedProducts().values()));
  }

  deselectProducts() {
    this.selectedProducts.set(new Map());
  }

  selectProduct(product: ProductFullDTOAndTags) {
    const currentlySelectedProducts = this.selectedProducts();
    if (currentlySelectedProducts.has(product.id)) {
      currentlySelectedProducts.delete(product.id);
      this.selectedProducts.set(new Map(currentlySelectedProducts));
    } else {
      currentlySelectedProducts.set(product.id, product);
      this.selectedProducts.set(new Map(currentlySelectedProducts));
    }
  }

  selectAction($event: MultiSelectButtonAction) {
    const currentProducts = this.productsList();
    if (!currentProducts || currentProducts.length === 0) {
      return;
    }
    const currentlySelectedProducts = this.selectedProducts();
    switch ($event) {
      case 'selectPage':
        currentProducts.forEach(item => {
          currentlySelectedProducts.set(item.id, item);
        });
        this.selectedProducts.set(new Map(currentlySelectedProducts));
        break;
      case 'deselectAll':
        this.selectedProducts.set(new Map());
        break;
      case 'deselectPage':
      default:
        currentProducts.forEach(item => {
          currentlySelectedProducts.delete(item.id);
        });
        this.selectedProducts.set(new Map(currentlySelectedProducts));
        break;
    }
  }

  applyColumns() {
    this.allOrderOptions = this.refreshAllOrderOptions();
    this.saveSelectedColumns('productListSelectedColumnsV1');
    this.refreshColumnFilters();
  }

  initColumns(attributeMap: AttributeMapDTO, preselectedColumns: IDAndTypeInterface[] | null) {
    this.columns = [];

    const allColumns: string[] = [
      ...getPropsFromDTO('', Object.keys(new ProductFullDTO())),
      ...getPropsFromDTO('manufacturer', Object.keys(new CompanyDTO())),
      ...getPropsFromDTO('manufacturer.address', Object.keys(new AddressDTO())),
    ];

    for (const col of allColumns) {
      const theCol = this.columnDefaults.find(defCol => defCol.id === col);
      if (theCol) {
        this.columns.push({ ...theCol });
      } else if (!col.startsWith('_')) {
        this.columns.push({ id: col, title: col, selected: false, type: 'PROPERTY' });
      }
    }

    this.columns.push(...createAttributeColumns(attributeMap.productAttributes, 'PRODUCT_ATTRIBUTE'));

    if (preselectedColumns) {
      // if there are preselectedColumns by the user then Ids should be matching and those shall be selected
      preselectedColumns.forEach(preCol => {
        const matchingCol = this.columns.find(col => isColumnMatch(col, preCol));
        if (matchingCol) {
          matchingCol.selected = true;
          this.selectedColumns.push(matchingCol);
        }
      });
    } else {
      this.selectedColumns = this.columns.filter(column => column.selected);
    }

    this.initColumnFilterMap('productFilter');

    this.applyColumns();
  }

  isFilteringAllowed(column: SbProductListColumn) {
    if (!column) {
      return false;
    }
    //     return (
    //       column.type === 'DEVICE_ATTRIBUTE' ||
    //       column.type === 'LOCATION_ATTRIBUTE' ||
    //       column.type === 'COMPANY_ATTRIBUTE' ||
    //       (column.type === 'PROPERTY' && column.canFilter)
    //     );
    return false; // implement when filtering is possible
  }

  // filter panel methods

  applyFiltering(filterMap: Map<string, any[]>) {
    this.setRequestOptions(filterMap);
    this.updateColumnFilters();
    this.requestDebounced();
  }

  setRequestOptions(filterMap: Map<string, any[]>) {
    // property filter
    // Id filter
    this.selectedCompany = filterMap.get('company')?.[0];
    this.filteredProductCategories = filterMap.get('productCategories');
    // search
    this.search = filterMap.get('search')?.[0];
    this.exactMatch = filterMap.get('exactMatch')?.[0];
  }

  private updateColumnFilters(): void {
    // order
    this.orderOption.set({ ...this.filterPanelOrderOption() });
    // columnFilter
    if (this.removableColumnFilters?.length > 0) {
      this.removableColumnFilters.forEach(filter => this.columnFiltersMap.delete(filter));
    }
    if (this.isInModalContext) {
      return;
    }
    this.saveColumnFilterToQueryParam('companyFilter');
  }
}
